import { IShipmentRepository } from '../../domain/repositories/IShipmentRepository';
import { Shipment, ShipmentStatus } from '../../domain/entities/Shipment';
import { IContainer } from '../../domain/entities/Container';
import { useAuth } from '../../composables/useAuth';
import { doc, getDoc, Unsubscribe } from 'firebase/firestore';
import { FirebaseProvider } from '../../infrastructure/providers/FirebaseProvider';
import type { ShipmentFile, CreateShipmentFileDTO } from '../../domain/entities/ShipmentFile';
import { booleanFieldDefaults } from '../../utils/fields/booleanFieldDefaults';
import { resolveStatusList } from '../../utils/shipmentStatusSets';

export class ShipmentService {
  constructor(private shipmentRepository: IShipmentRepository) {}

  async getShipments(filters?: Partial<Shipment>, page: number = 1, pageSize: number = 10, companyId?: string) {
    // Retornamos todos los items sin paginar; el total es el largo de la lista
    // (antes se pedía aparte y costaba una segunda lectura completa de la colección)
    const items = await this.shipmentRepository.findAll(filters, companyId);
    
    return {
      items: items,
      total: items.length,
      page: 1,
      pageSize: items.length,
      totalPages: 1
    };
  }

  async getShipmentById(id: string, companyId?: string) {
    return this.shipmentRepository.findById(id, companyId);
  }

  async createShipment(shipment: Omit<Shipment, 'id'>, companyId?: string) {
    const { currentUser } = useAuth();

    if (!currentUser.value) {
      throw new Error('User must be authenticated to create shipments');
    }

    const data: any = {
      ...shipment,
      consolidationType: shipment.consolidationType || 'standalone'
    };

    if (!data.status && companyId) {
      data.status = await ShipmentService.resolveCreationStatus(companyId, data.transportMode || '', data);
    }

    if (companyId) {
      Object.assign(data, await ShipmentService.resolveBooleanDefaults(companyId, data));
    }

    return this.shipmentRepository.create(data, companyId, currentUser.value.uid);
  }

  async updateShipment(id: string, shipment: Partial<Shipment>, companyId?: string) {
    const { currentUser } = useAuth();
    
    if (!currentUser.value) {
      throw new Error('User must be authenticated to update shipments');
    }
    
    return this.shipmentRepository.updateShipment(
      id, 
      shipment, 
      companyId,
      currentUser.value.uid,
      currentUser.value.name,
      currentUser.value.lastName
    );
  }

  async deleteShipment(id: string, companyId?: string) {
    const { currentUser } = useAuth();
    
    if (!currentUser.value) {
      throw new Error('User must be authenticated to delete shipments');
    }
    
    return this.shipmentRepository.delete(
      id, 
      companyId, 
      currentUser.value.uid,
      currentUser.value.name,
      currentUser.value.lastName
    );
  }

  async reactivateShipment(id: string, companyId: string) {
    const { currentUser } = useAuth();

    if (!currentUser.value) {
      throw new Error('User must be authenticated to reactivate shipments');
    }

    return this.shipmentRepository.reactivateShipment(id, companyId, {
      uid: currentUser.value.uid,
      email: currentUser.value.email,
      name: currentUser.value.name,
      lastName: currentUser.value.lastName
    });
  }

  async getNextShipmentNumber(companyId?: string) {
    return this.shipmentRepository.getNextShipmentNumber(companyId);
  }

  // Active Users methods
  async registerActiveUser(
    shipmentId: string,
    userId: string,
    userName: string,
    companyId?: string,
    lastName?: string
  ): Promise<void> {
    await this.shipmentRepository.registerActiveUser(
      shipmentId,
      userId,
      userName,
      companyId,
      lastName
    );
  }

  async removeActiveUser(shipmentId: string, userId: string, companyId?: string) {
    return this.shipmentRepository.removeActiveUser(shipmentId, userId, companyId);
  }

  subscribeToActiveUsers(
    shipmentId: string,
    callback: (users: Array<{ userId: string; userName: string; lastSeen: Date; createdAt: Date, isActive: boolean }>) => void,
    companyId?: string
  ): () => void {
    return this.shipmentRepository.subscribeToActiveUsers(shipmentId, callback, companyId);
  }

  subscribeToShipment(shipmentId: string, companyId: string | undefined, callback: (shipment: Shipment) => void): Unsubscribe {
    return this.shipmentRepository.subscribeToShipment(shipmentId, companyId, callback);
  }

  // Container methods
  async getContainers(shipmentId: string, companyId: string) {
    return await this.shipmentRepository.getContainers(shipmentId, companyId);
  }

  async addContainer(
    shipmentId: string, 
    containerData: Omit<IContainer, 'id'>, 
    companyId: string,
    userId?: string,
    name?: string,
    lastName?: string,
    isImported: boolean = false
  ) {
    // Si no se proporcionan los datos del usuario, intentamos obtenerlos
    if (!userId || !name || !lastName) {
      const { currentUser } = useAuth();
      
      if (!currentUser.value) {
        throw new Error('User must be authenticated to add containers');
      }

      userId = currentUser.value.uid;
      name = currentUser.value.name;
      lastName = currentUser.value.lastName;
    }

    try {
      const result = await this.shipmentRepository.addContainer(
        shipmentId, 
        containerData, 
        companyId,
        userId,
        name,
        lastName,
        isImported
      );

      return result;
    } catch (error) {
      console.error('❌ Error en ShipmentService.addContainer:', error);
      throw error;
    }
  }

  async updateContainer(
    shipmentId: string, 
    containerId: string, 
    containerData: Partial<IContainer>, 
    companyId: string,
    userId?: string,
    name?: string,
    lastName?: string
  ) {
    await this.shipmentRepository.updateContainer(
      shipmentId, 
      containerId, 
      containerData, 
      companyId,
      userId,
      name,
      lastName
    );
  }

  async deleteContainer(
    shipmentId: string, 
    containerId: string, 
    companyId: string,
    userId?: string,
    name?: string,
    lastName?: string
  ) {
    await this.shipmentRepository.deleteContainer(
      shipmentId, 
      containerId, 
      companyId,
      userId,
      name,
      lastName
    );
  }

  async addDeletedContainer(
    shipmentId: string, 
    containerNumber: string, 
    companyId: string,
    userId?: string,
    name?: string,
    lastName?: string
  ) {
    await this.shipmentRepository.addDeletedContainer(
      shipmentId, 
      containerNumber, 
      companyId,
      userId,
      name,
      lastName
    );
  }

  getInitials(name: string, lastName?: string): string {
    const nameInitial = name ? name.trim().charAt(0) : '';
    const lastNameInitial = lastName ? lastName.trim().charAt(0) : '';
    return lastNameInitial ? `${nameInitial}${lastNameInitial}`.toUpperCase() : nameInitial.toUpperCase();
  }

  async getNotifications(shipmentId: string, companyId: string, userId: string) {
    return await this.shipmentRepository.getNotifications(shipmentId, companyId, userId);
  }

  // ===== FILE MANAGEMENT METHODS =====

  /**
   * Get all files for a shipment
   */
  async getShipmentFiles(shipmentId: string, companyId: string): Promise<ShipmentFile[]> {
    return await this.shipmentRepository.getShipmentFiles(shipmentId, companyId);
  }

  /**
   * Upload a file for a shipment
   */
  async uploadShipmentFile(
    fileData: CreateShipmentFileDTO,
    onProgress?: (progress: number) => void
  ): Promise<ShipmentFile> {
    return await this.shipmentRepository.uploadShipmentFile(fileData, onProgress);
  }

  /**
   * Delete a file from a shipment
   */
  async deleteShipmentFile(
    shipmentId: string,
    fileId: string,
    companyId: string
  ): Promise<void> {
    const { currentUser } = useAuth();
    const deletedBy = currentUser.value
      ? {
          id: currentUser.value.uid,
          name: currentUser.value.name,
          lastName: currentUser.value.lastName
        }
      : undefined;
    await this.shipmentRepository.deleteShipmentFile(shipmentId, fileId, companyId, deletedBy);
  }

  /**
   * Update file type for a shipment file
   */
  async updateShipmentFileType(
    shipmentId: string,
    fileId: string,
    fileType: string,
    companyId: string,
    additionalFileTypes: string[] = []
  ): Promise<void> {
    await this.shipmentRepository.updateShipmentFileType(shipmentId, fileId, fileType, companyId, additionalFileTypes);
  }

  /**
   * Cambia el nombre que se muestra de un documento (solo visual)
   */
  async updateShipmentFileDisplayName(
    shipmentId: string,
    fileId: string,
    companyId: string,
    displayName: string
  ): Promise<void> {
    await this.shipmentRepository.updateShipmentFileDisplayName(shipmentId, fileId, companyId, displayName);
  }

  /**
   * Guarda la extracción de un tipo adicional sobre un archivo que contiene varios documentos
   */
  async saveShipmentFileAdditionalExtraction(
    shipmentId: string,
    fileId: string,
    documentType: string,
    data: any,
    companyId: string
  ): Promise<void> {
    await this.shipmentRepository.saveShipmentFileAdditionalExtraction(shipmentId, fileId, documentType, data, companyId);
  }

  async markFileReviewed(
    shipmentId: string,
    fileId: string,
    companyId: string,
    reviewer: { uid: string; name: string; lastName: string }
  ): Promise<void> {
    await this.shipmentRepository.markShipmentFileReviewed(shipmentId, fileId, companyId, reviewer);
  }

  async unmarkFileReviewed(
    shipmentId: string,
    fileId: string,
    companyId: string
  ): Promise<void> {
    await this.shipmentRepository.unmarkShipmentFileReviewed(shipmentId, fileId, companyId);
  }

  /**
   * Get a single file by ID
   */
  async getShipmentFileById(
    shipmentId: string,
    fileId: string,
    companyId: string
  ): Promise<ShipmentFile | null> {
    return await this.shipmentRepository.getShipmentFileById(shipmentId, fileId, companyId);
  }

  // ===== DOCUMENT FOLDER METHODS =====

  async getDocumentFolders(shipmentId: string, companyId: string) {
    return await this.shipmentRepository.getDocumentFolders(shipmentId, companyId);
  }

  async createDocumentFolder(
    shipmentId: string,
    companyId: string,
    name: string,
    parentFolderId: string | null,
    createdBy: string
  ) {
    return await this.shipmentRepository.createDocumentFolder(shipmentId, companyId, name, parentFolderId, createdBy);
  }

  async renameDocumentFolder(shipmentId: string, companyId: string, folderId: string, newName: string) {
    await this.shipmentRepository.renameDocumentFolder(shipmentId, companyId, folderId, newName);
  }

  async updateDocumentFolderParent(shipmentId: string, companyId: string, folderId: string, parentFolderId: string | null) {
    await this.shipmentRepository.updateDocumentFolderParent(shipmentId, companyId, folderId, parentFolderId);
  }

  async deleteDocumentFolder(shipmentId: string, companyId: string, folderId: string) {
    await this.shipmentRepository.deleteDocumentFolder(shipmentId, companyId, folderId);
  }

  async moveFileToFolder(shipmentId: string, companyId: string, fileId: string, folderId: string | null) {
    await this.shipmentRepository.moveFileToFolder(shipmentId, companyId, fileId, folderId);
  }

  async moveFilesToFolder(shipmentId: string, companyId: string, fileIds: string[], folderId: string | null) {
    await this.shipmentRepository.moveFilesToFolder(shipmentId, companyId, fileIds, folderId);
  }

  // ===== INVOICE MANAGEMENT METHODS =====

  /**
   * Get all invoices for a shipment
   */
  async getShipmentInvoices(shipmentId: string, companyId: string): Promise<any[]> {
    return await this.shipmentRepository.getShipmentInvoices(shipmentId, companyId);
  }

  /**
   * Get a specific invoice by ID
   */
  async getInvoiceById(
    shipmentId: string,
    invoiceId: string,
    companyId: string
  ): Promise<any> {
    return await this.shipmentRepository.getInvoiceById(shipmentId, invoiceId, companyId);
  }

  // ===== Consolidation Methods =====

  async getMasterShipments(companyId: string) {
    return this.shipmentRepository.findMasters(companyId);
  }

  async getHouseShipments(masterShipmentId: string, companyId: string) {
    return this.shipmentRepository.findHousesByMaster(masterShipmentId, companyId);
  }

  async createHouseFromMaster(
    masterShipmentId: string,
    houseData: Partial<Shipment>,
    companyId: string
  ) {
    const { currentUser } = useAuth();

    if (!currentUser.value) {
      throw new Error('User must be authenticated to create house shipments');
    }

    const fullName = `${currentUser.value.name || ''} ${currentUser.value.lastName || ''}`.trim();

    return this.shipmentRepository.createHouseFromMaster(
      masterShipmentId,
      houseData,
      companyId,
      fullName || currentUser.value.uid
    );
  }

  async unlinkHouseFromMaster(
    houseShipmentId: string,
    masterShipmentId: string,
    companyId: string
  ) {
    return this.shipmentRepository.unlinkHouseFromMaster(
      houseShipmentId,
      masterShipmentId,
      companyId
    );
  }

  async addHouseRefToMaster(
    masterShipmentId: string,
    houseRef: { id: string; idNumber: string; hbl?: string | null; hawb?: string | null; status?: string; consignee?: string | null },
    companyId: string
  ) {
    return this.shipmentRepository.addHouseRefToMaster(masterShipmentId, houseRef, companyId);
  }

  private static mapTransportModeToKey(transportMode: string): 'AIR' | 'SEA' | 'ROA' {
    if (!transportMode) return 'SEA'
    const n = transportMode.toLowerCase()
    if (n.includes('ocean') || n.includes('sea') || n.includes('marítimo') || n.includes('maritimo')) return 'SEA'
    if (n.includes('air') || n.includes('aéreo') || n.includes('aereo')) return 'AIR'
    if (n.includes('otm') || n.includes('road') || n.includes('terrestre') || n.includes('truck')) return 'ROA'
    return 'SEA'
  }

  static async resolveCreationStatus(companyId: string, transportMode: string = '', shipmentData?: Record<string, unknown>): Promise<string> {
    try {
      const db = FirebaseProvider.getInstance().getFirestore()
      const snap = await getDoc(doc(db, 'Companies', companyId, 'config', 'SHIPMENT_STATUS'))
      if (!snap.exists()) return ShipmentStatus.BOOKING_REQUEST

      const data = snap.data()
      const modeKey = ShipmentService.mapTransportModeToKey(transportMode)

      const resolved = resolveStatusList(data, { ...(shipmentData || {}), transportMode })
      let statuses: any[] = []
      if (resolved?.source === 'set') {
        statuses = resolved.statuses
      } else if (data.statusByMode?.[modeKey]) {
        statuses = data.statusByMode[modeKey]
      } else if (data.status) {
        statuses = data.status
      }

      const creationStatus = statuses
        .filter((s: any) => s.visible !== false)
        .find((s: any) => s.triggerType === 'creation')

      return creationStatus?.id || ShipmentStatus.BOOKING_REQUEST
    } catch {
      return ShipmentStatus.BOOKING_REQUEST
    }
  }

  /**
   * Valor inicial de los campos boolean del embarque, leído de la configuración de
   * la compañía. Ver utils/fields/booleanFieldDefaults.
   */
  static async resolveBooleanDefaults(
    companyId: string,
    data: Record<string, any> = {}
  ): Promise<Record<string, boolean>> {
    try {
      const db = FirebaseProvider.getInstance().getFirestore()
      const snap = await getDoc(doc(db, 'Companies', companyId, 'config', 'systemFields'))
      if (!snap.exists()) return {}

      const shipments = snap.data()?.shipments
      const fields = Array.isArray(shipments)
        ? shipments
        : shipments && typeof shipments === 'object' ? Object.values(shipments) : []

      return booleanFieldDefaults(fields as any[], data)
    } catch {
      return {}
    }
  }
}